/**
 * src/pages/ProjectDetail.jsx — the session timeline and reference vault.
 *
 * This is the page the whole product is built around: everything about one
 * piece of work in one place. Three parts:
 *
 *  - the header, where the artist moves the project through active → complete
 *  - the session timeline, in chronological order, so progress is legible
 *  - the vault: references, progress shots and healed photos, each uploaded
 *    into its own bucket so they never get muddled
 */
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, uploadFile } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useApi } from '../lib/useApi.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, PageHeader, Pill } from '../components/ui.jsx';

/** The three vault buckets, in the order a piece of work moves through them. */
const VAULT = [
  { type: 'reference', label: 'References', hint: 'What the client brought in.' },
  { type: 'progress_photo', label: 'Progress', hint: 'Shot at the end of each sitting.' },
  { type: 'healed_photo', label: 'Healed', hint: 'How the skin actually took it.' },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const { timezone } = useAuth();
  const detail = useApi(`/api/projects/${id}`);
  const media = useApi(`/api/projects/${id}/media`);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const project = detail.data?.project;

  /**
   * Patches the project and refreshes.
   *
   * @param {Record<string, unknown>} patch
   */
  const save = async (patch) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.patch(`/api/projects/${id}`, patch);
      detail.reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Loader loading={detail.loading} error={detail.error}>
      {project ? (
        <>
          <PageHeader
            title={project.title}
            subtitle={
              <>
                {project.clientName} · {project.artistName}
                {project.bodyLocation ? ` · ${project.bodyLocation}` : ''}
              </>
            }
            actions={
              <>
                <select
                  value={project.status}
                  onChange={(e) => save({ status: e.target.value })}
                  disabled={busy}
                  aria-label="Project status"
                  style={{ width: 140 }}
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="complete">Complete</option>
                </select>
                <Link to="/projects" className="btn">Back</Link>
              </>
            }
          />

          <ErrorBanner>{actionError}</ErrorBanner>

          <div className="stack-lg">
            <section className="card">
              <div className="row-between" style={{ marginBottom: 14 }}>
                <h2>
                  Sessions · {detail.data.appointments.length}
                  {project.estimatedSessions ? ` of ~${project.estimatedSessions}` : ''}
                </h2>
                <Link to="/calendar" className="btn btn-sm">Book another</Link>
              </div>

              {detail.data.appointments.length === 0 ? (
                <Empty>No sittings booked yet.</Empty>
              ) : (
                <ol className="stack" style={{ margin: 0, paddingLeft: 20 }}>
                  {detail.data.appointments.map((a, index) => (
                    <li key={a.id}>
                      <div className="row-between">
                        <div>
                          <strong>Session {index + 1}</strong>
                          <span className="muted"> · {formatDateTime(a.starts_at, timezone)}</span>
                          {a.notes ? <div className="muted small">{a.notes}</div> : null}
                        </div>
                        <Pill status={a.status} />
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {project.notes ? (
              <section className="card">
                <h2 style={{ marginBottom: 8 }}>Notes</h2>
                <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{project.notes}</p>
              </section>
            ) : null}

            {VAULT.map((bucket) => (
              <VaultSection
                key={bucket.type}
                projectId={id}
                bucket={bucket}
                items={media.data?.media?.[bucket.type] ?? []}
                loading={media.loading}
                error={media.error}
                onChanged={media.reload}
              />
            ))}
          </div>
        </>
      ) : null}
    </Loader>
  );
}

/**
 * One bucket of the reference vault, with upload and delete.
 *
 * @param {{ projectId: string, bucket: { type: string, label: string, hint: string },
 *   items: object[], loading: boolean, error: string|null, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function VaultSection({ projectId, bucket, items, loading, error, onChanged }) {
  const { timezone } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  /**
   * Uploads every file the artist selected, one at a time.
   *
   * Sequential rather than parallel on purpose: a batch of phone photos in
   * parallel is a lot of concurrent multi-megabyte PUTs from a device that may
   * be on studio wifi.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onFiles = async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        await uploadFile(file, { assetType: bucket.type, projectId });
      }
      onChanged();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      // Reset the input so selecting the same file again still fires onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (mediaId) => {
    setUploadError(null);
    try {
      await api.del(`/api/uploads/${mediaId}`);
      onChanged();
    } catch (err) {
      setUploadError(err.message);
    }
  };

  return (
    <section className="card">
      <div className="row-between" style={{ marginBottom: 14 }}>
        <div>
          <h2>{bucket.label}</h2>
          <p className="muted small">{bucket.hint}</p>
        </div>
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onFiles}
            style={{ display: 'none' }}
            id={`upload-${bucket.type}`}
          />
          <button
            type="button"
            className="btn btn-sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Add photos'}
          </button>
        </>
      </div>

      <ErrorBanner>{uploadError ?? error}</ErrorBanner>

      {loading ? (
        <div className="empty">Loading…</div>
      ) : items.length === 0 ? (
        <Empty>Nothing here yet.</Empty>
      ) : (
        <div className="gallery">
          {items.map((m) => (
            <figure key={m.id} style={{ margin: 0 }}>
              <a href={m.url} target="_blank" rel="noreferrer">
                <img src={m.url} alt={bucket.label} loading="lazy" />
              </a>
              <figcaption className="row-between small muted" style={{ marginTop: 4 }}>
                {formatDate(m.created_at, timezone)}
                <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(m.id)}>
                  Delete
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
